import { ReactPlugin, withAITracking } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, DistributedTracingModes, SeverityLevel } from '@microsoft/applicationinsights-web';
import get from 'lodash-es/get';
import { getResortLocation } from '../store/componentStores/CurrentLodge/currentLodge.selectors';
import { getCheckinDateSelection, getCheckoutDateSelection } from '../store/componentStores/Dates/dates.selectors';
import { getAdultsCount, getKidsAges } from '../store/componentStores/Guests/guests.selectors';
import { getDefaultOfferCode } from '../store/componentStores/Offer/offer.selectors';
import { getSelectedSuiteSuiteCode } from '../store/componentStores/Suite/suite.selectors';
import { getStore } from '../store/storeRef';
import { maskPIIProperties } from './personallyIdentifiableInformation/piiUtils';

export const EVENT_CATEGORIES = {
  bookingEngineBookReservationError: 'OnBookReservationError',
  OnVersionJEventError: 'OnVersionJEventError',
  OnVersionHEventError: 'OnVersionHEventError',
  onI4IframeError: 'OnI4IframeError',
  on3DSChallengeModalOpen: 'On3DSChallengeModalOpen',
  on3DSChallengeModalCancel: 'On3DSChallengeModalCancel',
  on3DSPaymentPageLoaded: 'On3DSPaymentPageLoaded',
  on3DSPaymentSectionLoaded: 'On3DSPaymentSectionLoaded',
  on3DSBackendHandling: 'On3DSBackendHandling',
  on3DSIFrameInitialized: 'On3DSIFrameInitialized',
  on3DSPaymentTypeSelected: 'On3DSPaymentTypeSelected',
  on3DSBookingSuccess: 'On3DSBookingSuccess',
  paymentAPIError: 'PaymentAPIError',
  failedToLoadPaymentMethods: 'FailedToLoadPaymentMethods',
  apiGeneralError: 'APIGeneralError',
  paymentMethodsDisabled: 'PaymentMethodsDisabled',
  affirmWindowError: 'AffirmWindowError',
  activitiesLoaded: 'ActivitiesLoaded'
};

export const ACTIVITIES_UFF_ERROR_TYPES = {
  noPackagesFound: 'NoPackagesFound'
};

const reactPlugin = new ReactPlugin();

const ai = new ApplicationInsights({
  config: {
    instrumentationKey: process.env.REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY,
    enableAutoRouteTracking: true,
    extensions: [reactPlugin],
    distributedTracingMode: DistributedTracingModes.W3C,
    enableCorsCorrelation: true,
    correlationHeaderDomains: ['*.greatwolf.com'],
    disableAjaxTracking: true,
    disableFetchTracking: true
  }
});

ai.addTelemetryInitializer(function(envelope) {
  maskPIIProperties(envelope);
});

ai.loadAppInsights();

const appInsights = ai.appInsights;

export const trackError = (eventName, errorMessage) => {
  try {
    appInsights.trackEvent({
      name: eventName,
      properties: { error: errorMessage }
    });
  } catch (error) {
    console.error('Error tracking error:', error);
  }
};

export const trackInteractionFailureEvent = (errorMessage, eventName) => {
  trackError(eventName, errorMessage);
};

/**
 * @param {String} eventName
 * @param {String} errorMessage
 * @param {Object} errorObject
 */
export const trackReservationFailureEvent = (eventName, errorMessage, errorObject) => {
  // We stringify so we force the Error object to materialize all of it's properties
  // Then we need to parse it to a JSON object so maskPIIProperties knows how to traverse and mask it's PII properties
  const stringfiedErrorObject = JSON.stringify(errorObject);
  errorObject = maskPIIProperties(JSON.parse(stringfiedErrorObject));
  trackError(eventName, `${errorMessage} Error: ${JSON.stringify(errorObject)}`);
};

/**
 * Track I4Iframe error or event
 * @param {String} eventName
 * @param {Object} properties
 */
export const trackI4IframeAppInsightsEvent = (eventName, properties) => {
  try {
    if (eventName === EVENT_CATEGORIES.onI4IframeError) {
      trackError(EVENT_CATEGORIES.onI4IframeError, JSON.stringify(properties));
    } else {
      appInsights.trackEvent({ name: eventName, properties });
    }
  } catch (error) {
    console.error('Error tracking I4Iframe event:', error);
  }
};

/**
 * Track error on availability, highest rates and detailed availability
 * @param {Object} state Redux state object
 * @param {String} errorTitle Error title shown on availability error
 */
export const availabilityTrackError = (state, errorType, errorTitle, isDetailedAvailabilty = false) => {
  const errorMessage = !isDetailedAvailabilty
    ? get(state, 'availabilityErrorMessage', '')
    : get(state, 'suiteAvailabilityErrorMessage', '');
  trackPlanPageFailureEvent(errorType, errorTitle, errorMessage, isDetailedAvailabilty);
};

export const trackPlanPageFailureEvent = (errorType, errorTitle, errorMessage, isDetailedAvailabilty) => {
  // fail silently
  try {
    const { dates, guests, offer, currentLodge, suite } = getStore().getState();

    // Gather information from user's selection
    let userSelection = {
      arrival: getCheckinDateSelection(dates),
      departure: getCheckoutDateSelection(dates),
      adultCount: getAdultsCount(guests),
      kidsAges: getKidsAges(guests),
      offerCode: getDefaultOfferCode(offer),
      property: getResortLocation(currentLodge)
    };

    if (isDetailedAvailabilty) {
      userSelection.suiteCode = getSelectedSuiteSuiteCode(suite);
    }

    const userChoice = JSON.stringify(userSelection);
    const errorLogInfo = `Error on plan page, ${errorTitle}. ${errorMessage};`;

    appInsights.trackException({
      exception: new Error(errorLogInfo),
      severityLevel: SeverityLevel.Error,
      properties: {
        component: 'BookingEngine',
        method: errorType,
        message: userChoice
      }
    });
  } catch (error) {
    console.error('Error tracking error:', error);
  }
};

export const generalAppInsightsTrack = (errorMessage, eventName) => {
  trackError(eventName, errorMessage);
};

let _affirmWindowStatusTracked = false;

export const trackAffirmWindowLoaded = ({ lodge, affirmOffered } = {}) => {
  if (_affirmWindowStatusTracked) return;
  _affirmWindowStatusTracked = true;
  try {
    appInsights.trackEvent({
      name: 'AffirmWindowLoaded',
      properties: {
        component: 'BookingEngine',
        method: 'AffirmWindowLoaded',
        lodge,
        affirmOffered
      }
    });
  } catch (error) {
    console.error('Error tracking Affirm window loaded:', error);
  }
};

export const trackAffirmWindowError = ({ lodge, affirmOffered } = {}) => {
  if (_affirmWindowStatusTracked) return;
  _affirmWindowStatusTracked = true;
  try {
    appInsights.trackException({
      exception: new Error('Affirm not loaded on payment page'),
      severityLevel: SeverityLevel.Error,
      properties: {
        component: 'BookingEngine',
        method: 'AffirmWindowError',
        lodge,
        affirmOffered
      }
    });
  } catch (error) {
    console.error('Error tracking Affirm window error:', error);
  }
};

/**
 * @param {String} errorMessage error if there is a JS error
 * @param {String} component component name
 * @param {String} errorType error type
 * @param {Object} message data to be logged
 */
export const trackErrorWithProperties = (errorMessage, component, errorType, message) => {
  try {
    const errorLogInfo = `Error on ${component}. ${errorMessage};`;
    appInsights.trackException({
      exception: new Error(errorLogInfo),
      severityLevel: SeverityLevel.Error,
      properties: {
        component,
        method: errorType,
        message: JSON.stringify(message)
      }
    });
  } catch (error) {
    console.error('Error tracking error:', error);
  }
};

/**
 * @param {String} eventName event name
 * @param {String} component component name
 * @param {Object} message data to be logged
 */
export const trackEventWithProperties = (eventName, component, message) => {
  try {
    appInsights.trackEvent({
      name: eventName,
      properties: {
        component,
        source: component,
        message: JSON.stringify(message)
      }
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

export const trackUserPaymentMissingToken = properties => {
  try {
    appInsights.trackEvent({ name: 'UserPaymentMissingToken', properties });
  } catch {
    // telemetry must never fail a booking
  }
};

export const trackUserPaymentTokenLateArrival = properties => {
  try {
    appInsights.trackEvent({ name: 'UserPaymentTokenLateArrival', properties });
  } catch {
    // telemetry must never fail a booking
  }
};

const withAppInsights = Component => withAITracking(reactPlugin, Component);

export default withAppInsights;
